import { GetListingV3PublicResponse } from '@kouto/types';
import { PaymentIntentError } from '../types';

/**
 * Matches a payment intent error to a specific listing by checking the error's
 * resourceGroupId or experienceId against the listing's structure:
 *
 *   Listing (id, experienceId?)
 *     └── ResourceGroup (resourceGroupId)
 *          └── Experience (experienceId)
 *
 * A listing can either be a simple experience (listing.experienceId is set,
 * resourceGroups may be empty) or a resource-group-based listing
 * (listing.experienceId is null, resourceGroups contain nested experiences).
 */
export const validatePaymentIntentError = (
  paymentIntentError: PaymentIntentError,
  listing?: GetListingV3PublicResponse,
) => {
  const { cartItemsError } = paymentIntentError;

  if (!cartItemsError || !listing) return null;

  return (
    cartItemsError.find((item) => {
      // Match by resourceGroupId against the listing's resource groups
      if (item.resourceGroupId && listing.resourceGroups) {
        return listing.resourceGroups.some(
          (rg) => rg.id === item.resourceGroupId,
        );
      }
      // Match by experienceId
      if (item.experienceId) {
        // Direct match: listing is a simple experience with a top-level experienceId
        if (
          listing.experienceId &&
          item.experienceId === listing.experienceId
        ) {
          return true;
        }
        // Nested match: experienceId lives within a resource group's experiences
        if (listing.resourceGroups) {
          return listing.resourceGroups.some((rg) =>
            rg.experiences?.some((exp) => exp.id === item.experienceId),
          );
        }
      }
      return false;
    }) || null
  );
};
